// src/pages/Kamen.tsx
import { Tab } from '@headlessui/react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import HeroSection from '@/components/HeroSection';
import SectionHeading from '@/components/SectionHeading';
import Gallery from '@/components/Gallery';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const Kamen = () => {
  const categories = [
    { id: 'tehnicni-kamen', name: 'Tehnični kamen' },
    { id: 'granit', name: 'Granit' },
    { id: 'okenske-police', name: 'Okenske police' },
    { id: 'stopnice', name: 'Stopnice' },
    { id: 'tlaki', name: 'Tlaki' },
  ];

  const categoryData = {
    'tehnicni-kamen': {
      title: 'Tehnični kamen',
      description: 'Trpežen kamen za tehnične rešitve.',
      images: [
        { 
          src: 'https://images.unsplash.com/photo-1600607687400-a5d391fe82bf?q=80&w=2070', 
          alt: 'Tehnični kamen' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1572887423814-7cadd5dcea45?q=80&w=2071', 
          alt: 'Tehnični kamen v kuhinji' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1604578762246-41134e37f9cc?q=80&w=2070', 
          alt: 'Tehnični kamen v kopalnici' 
        },
      ]
    },
    'granit': {
      title: 'Granit',
      description: 'Klasična lepota granita.',
      images: [
        { 
          src: 'https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?q=80&w=2070', 
          alt: 'Granit' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1608934267874-fa3cfd8d7b85?q=80&w=2069', 
          alt: 'Granitni bloki' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1597669538481-56831a2b5eb8?q=80&w=2069', 
          alt: 'Granit v kuhinji' 
        },
      ]
    },
    'okenske-police': {
      title: 'Okenske police',
      description: 'Okenske police iz naravnega kamna.',
      images: [
        { 
          src: 'https://images.unsplash.com/photo-1577909251788-2a3c3676c9b6?q=80&w=2070', 
          alt: 'Okenske police' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1518065602997-73d6403c9099?q=80&w=2070', 
          alt: 'Okenske police iz marmorja' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1471623432079-b009d30b911d?q=80&w=2070', 
          alt: 'Okenske police iz granita' 
        },
      ]
    },
    'stopnice': {
      title: 'Stopnice',
      description: 'Stopnice za eleganten vstop.',
      images: [
        { 
          src: 'https://images.unsplash.com/photo-1567767292278-a4f21aa2d36e?q=80&w=2070', 
          alt: 'Kamnite stopnice' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1520033713672-e1b49634cdf5?q=80&w=2069', 
          alt: 'Marmorne stopnice' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1589834390005-5d4fb9bf3d32?q=80&w=2026', 
          alt: 'Granitne stopnice' 
        },
      ]
    },
    'tlaki': {
      title: 'Tlaki',
      description: 'Tlaki za zunanje in notranje površine.',
      images: [
        { 
          src: 'https://images.unsplash.com/photo-1598652339270-d6920d0723cb?q=80&w=2070', 
          alt: 'Kamniti tlaki' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1547127796-06bbeb4001ad?q=80&w=2070', 
          alt: 'Granitni tlaki' 
        },
        { 
          src: 'https://images.unsplash.com/photo-1614252235316-8c857d38b5f4?q=80&w=2070', 
          alt: 'Tlaki v vrtu' 
        },
      ]
    },
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <main className="pt-16">
        <HeroSection 
          title="Naravni kamen za vse vaše projekte"
          subtitle="Trajnost in eleganca v vsakem detajlu"
          imageSrc="https://images.unsplash.com/photo-1591128213901-44cadd7d2067?q=80&w=2070"
        />

        <section className="py-16">
          <div className="container-custom">
            <SectionHeading 
              title="Naša ponudba kamna"
              subtitle="Vrhunski naravni kamen za notranjo in zunanjo uporabo"
            />
            
            <Tab.Group>
              <Tab.List className="flex space-x-1 rounded-xl bg-stone-100 p-1 mb-8">
                {categories.map((category) => (
                  <Tab
                    key={category.id}
                    className={({ selected }) =>
                      `w-full rounded-lg py-3 text-sm font-medium leading-5 
                      ${
                        selected
                          ? 'bg-white shadow text-stone-800'
                          : 'text-stone-600 hover:bg-white/[0.12] hover:text-stone-800'
                      }`
                    }
                  >
                    {category.name}
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels className="mt-2">
                {Object.entries(categoryData).map(([id, data]) => (
                  <Tab.Panel
                    key={id}
                    className="rounded-xl p-3"
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="mb-10">
                        <h3 className="text-2xl font-bold text-stone-800 mb-3">{data.title}</h3>
                        <p className="text-lg text-stone-600 mb-6">{data.description}</p>
                        <Gallery images={data.images} />
                        <div className="mt-8 flex justify-center">
                          <Button asChild>
                            <a href="/kontakt" className="flex items-center gap-2">
                              Zahtevaj ponudbo <ArrowRight size={16} />
                            </a>
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Kamen;